import type { ProjectSettingsFormValues } from '../Context';
import type { Path } from 'react-hook-form';

import React from 'react';
import { useWatch } from 'react-hook-form';

import useUniqueId from '@core/hooks/useUniqueId';
import classy from '@core/utils/classy';

import DemoGate from '@routes/SuperHub/components/PlanAccess/DemoGate';
import { FormRow, Page, Fieldset } from '@routes/SuperHub/Settings/components';

import CodeInput from '@ui/CodeInput';
import CopyToClipboard from '@ui/CopyToClipboard';
import { RHFGroup } from '@ui/RHF';
import Toggle from '@ui/Toggle';

import { useProjectSettingsFormContext } from '../Context';
import useLiveUpdateProjectStore from '../useLiveUpdateProjectStore';

import classes from './index.module.scss';

const fields: Path<ProjectSettingsFormValues>[] = ['appearance.footer.readme_logo'];

export default function Footer({ customFormGate }: { customFormGate?: 'demo' | 'none' }) {
  const uid = useUniqueId('Footer');
  const { control } = useProjectSettingsFormContext();
  const readmeLogo = useWatch({ control, name: 'appearance.footer.readme_logo' });

  useLiveUpdateProjectStore({ fields, control });

  return (
    <Page>
      <DemoGate access={customFormGate === 'demo' ? 'locked' : 'open'} feature="footer">
        <Fieldset size="sm">
          <FormRow>
            <RHFGroup
              control={control}
              id={uid('branding')}
              label="Branding"
              name="appearance.footer.readme_logo"
              size="sm"
            >
              {({ field }) => (
                <Toggle
                  {...field}
                  checked={field.value === 'hide'}
                  isLabelMuted
                  label={<span>Hide ReadMe logo in site footer</span>}
                  onChange={event => {
                    field.onChange(event.target.checked ? 'hide' : 'show');
                  }}
                  size="sm"
                />
              )}
            </RHFGroup>

            <RHFGroup
              className={classy(
                classes['Footer-formGroup'],
                readmeLogo !== 'hide' && classes['Footer-formGroup_disabled'],
              )}
              control={control}
              data-testid="footer-html-input"
              description={<p>Will appear in a {'<footer />'} element on every page.</p>}
              id={uid('footer-html')}
              label="Footer HTML"
              name="appearance.custom_code.html.home_footer"
              size="sm"
            >
              {({ field }) => (
                <div className={classes['Footer-codeInput-container']}>
                  {readmeLogo === 'hide' && (
                    <CopyToClipboard className={classes['Footer-codeInput-copy']} text={field.value || ''} />
                  )}
                  <CodeInput
                    {...field}
                    className={classes['Footer-codeInput']}
                    initialValue={field.value}
                    language="html"
                    readOnly={readmeLogo !== 'hide'}
                  />
                </div>
              )}
            </RHFGroup>
          </FormRow>
        </Fieldset>
      </DemoGate>
    </Page>
  );
}
